import { CombinedGraphQLErrors, ServerError, ServerParseError } from '@apollo/client/errors';

/**
 * Custom error handler to replace the deprecated isApolloError function.
 *
 * isApolloError was deprecated in Apollo Client 3.9 and removed in Apollo Client 3.10.
 * In Apollo Client 4.x, we use the specific error classes with their static `is` methods.
 * This utility provides backward compatibility while using the new Apollo error handling approach.
 *
 * @param error - The error object to check
 * @returns boolean - True if the error is an Apollo-related error
 */
export const isApolloErrorInstance = (error: any): boolean => {
  if (!error) return false;

  // Use Apollo Client 4.x error type checking with static `is` methods
  return (
    CombinedGraphQLErrors.is(error) ||
    ServerError.is(error) ||
    ServerParseError.is(error) ||
    // Fallback to property checking for any other Apollo-related errors
    (error.message !== undefined &&
     (error.networkError !== undefined ||
      error.graphQLErrors !== undefined ||
      error.clientErrors !== undefined ||
      error.protocolErrors !== undefined ||
      error.name === 'ApolloError'))
  );
};

/**
 * Enhanced error handler that provides more detailed error information
 *
 * @param error - The error object to analyze
 * @returns object - Detailed error information including type, message, and network status
 */
export const analyzeError = (error: any) => {
  const isApolloErr = isApolloErrorInstance(error);
  const isGraphQLErr = CombinedGraphQLErrors.is(error);
  const isServerErr = ServerError.is(error);
  const isParseErr = ServerParseError.is(error);

  // Extract message from various possible sources
  let message = 'Unknown error';
  if (error?.message) {
    message = error.message;
  } else if (error?.cause?.message) {
    message = error.cause.message;
  } else if (isGraphQLErr && error?.errors?.[0]?.message) {
    message = error.errors[0].message;
  } else if (isServerErr && error?.bodyText) {
    message = error.bodyText;
  } else if (isParseErr && error?.bodyText) {
    message = error.bodyText;
  }

  return {
    isApolloError: isApolloErr,
    isGraphQLError: isGraphQLErr,
    isServerError: isServerErr,
    isParseError: isParseErr,
    isNetworkError: isApolloErr && (error.networkError !== undefined || isServerErr || isParseErr),
    message,
    networkError: isApolloErr ? error.networkError : undefined,
    graphQLErrors: isGraphQLErr ? error.errors : error.graphQLErrors,
    clientErrors: isApolloErr ? error.clientErrors : undefined,
    protocolErrors: isApolloErr ? error.protocolErrors : undefined,
    statusCode: error?.statusCode || (isServerErr ? error.statusCode : undefined),
    name: error?.name,
    cause: error?.cause,
    originalError: error
  };
};

/**
 * Extracts the cause message from an error object safely
 *
 * @param error - The error object to extract cause message from
 * @returns string - The cause message or undefined if not available
 */
export const getErrorCauseMessage = (error: any): string | undefined => {
  if (!error) return undefined;

  return error.cause?.message;
};

/**
 * Simple error type guard for Apollo errors
 * Alias of isApolloErrorInstance for backward compatibility
 */
export const isApolloError = isApolloErrorInstance;